import { MinusOutlined, PlusOutlined } from '@ant-design/icons'
import {Alert, Button, Form, Input, Select} from 'antd'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { CreativeBean, EventReportKeyColumnDescription, KeyFilterClause } from 'src/api/api'
import dictionariesApi from 'src/util/DictionariesApi'
import { enumView } from '../../../util/Util'
import adGroupApi from '../adGroup/AdGroupApi'
import adSizeApi from '../adSize/AdSizeApi'
import advertiserApi from '../advertiser/AdvertiserApi'
import campaignApi from '../campaign/CampaignApi'
import creativeApi from '../creative/CreativeApi'
import mediaAgencyApi from '../mediaAgency/MediaAgencyApi'
import sspApi from '../ssp/SspApi'
import './style.css'

const Option = Select.Option
const FormItem = Form.Item

const formatEnum = CreativeBean.FormatEnum
const KeyColumns = EventReportKeyColumnDescription.ColumnEnum
const clauseTypeEnum = KeyFilterClause.TypeEnum

const fromData = (resp: any): FilterData[] => resp.data?.map(item => ({ name: item.data.name }))

const keyLoadersMap = new Map<EventReportKeyColumnDescription.ColumnEnum, any>([
    [KeyColumns.SSP, () => sspApi.all().then(fromData)],
    [KeyColumns.MediaAgency, () => mediaAgencyApi.all().then(fromData)],
    [KeyColumns.Advertiser, () => advertiserApi.all().then(fromData)],
    [KeyColumns.Campaign, () => campaignApi.all().then(fromData)],
    [KeyColumns.AdGroup, () => adGroupApi.all().then(fromData)],
    [KeyColumns.Creative, () => creativeApi.all().then(fromData)],
    [KeyColumns.AdSize, () => adSizeApi.all().then(fromData)],
    [KeyColumns.Domain, () => []],
    [KeyColumns.Bundle, () => []],
    [KeyColumns.Country, () => dictionariesApi.countries().then(resp => resp.map(item => ({ id: item.code, name: item.name })))],
    [KeyColumns.Language, () => dictionariesApi.languages().then(resp => resp.map(item => ({ id: item.id, name: item.name })))],
    [KeyColumns.DeviceType, () => dictionariesApi.deviceTypes().then(resp => resp.map(item => ({ id: item.id, name: item.name })))],
    [KeyColumns.MediaType, () => Object.keys(formatEnum).map(item => ({ name: item }))],
    [KeyColumns.OS, () => []],
])

export interface FilterData {
    id: string | number | undefined
    name: string
}

export interface Filter {
    type: EventReportKeyColumnDescription.ColumnEnum | undefined
    clauseType: KeyFilterClause.TypeEnum | undefined
    loadedData: FilterData[] | undefined,
    values: (string | number)[]
}

interface Props {
    filters: Filter[]
    setFilters: (filters: Filter[]) => void
}

export const emptyFilter = () => {
    return {
        type: undefined,
        clauseType: undefined,
        loadedData: undefined,
        values: []
    }
}

export const getClauseTypeLabel = (type: KeyFilterClause.TypeEnum) => {
    switch (type) {
        case clauseTypeEnum.In: return 'Include'
        case clauseTypeEnum.NotIn: return 'Exclude'
        default: return 'Include'
    }
}

const Filters = ({ filters, setFilters }: Props) => {

    const [error, setError] = useState(undefined)

    useEffect(() => {
        let cancelled = false

        const loadMissing = async () => {
            const targets = filters
                .map((f, idx) => ({ f, idx }))
                .filter(({ f }) => !!f.type && f.loadedData === undefined && keyLoadersMap.has(f.type))

            if (targets.length === 0) return

            try {
                const loadedPairs = await Promise.all(
                    targets.map(async ({ f, idx }) => {
                        const loader = keyLoadersMap.get(f.type!)
                        const data = loader ? await loader() : []
                        return { idx, data }
                    }),
                )

                if (cancelled) return

                const next = [...filters]
                loadedPairs.forEach(({ idx, data }) => {
                    if (!next[idx]) return
                    if (next[idx].loadedData !== undefined) return
                    next[idx] = { ...next[idx], loadedData: data || [] }
                })

                setFilters(next)
            } catch (e: any) {
                if (!cancelled) {
                    setError(e?.error)
                }
            }
        }

        loadMissing()
        return () => { cancelled = true }
    }, [filters, setFilters])

    const setSelectedFilter = async (selected: EventReportKeyColumnDescription.ColumnEnum, index: number) => {
        try {
            const loader = keyLoadersMap.get(selected)
            const resp = await loader()

            const filter = filters[index]
            filter.type = selected
            filter.loadedData = resp
            filter.values = []

            const list = [...filters]
            setFilters(list)
        } catch (e: any) {
            setError(e.error)
        }
    }

    const clearSelectedFilter = (index: number) => {
        const filter = filters[index]
        filter.type = undefined
        filter.loadedData = []
        filter.values = []

        const list = [...filters]
        setFilters(list)
    }

    const setSelectedClauseType = (value: KeyFilterClause.TypeEnum, index: number) => {
        const filter = filters[index]
        filter.clauseType = value

        const list = [...filters]
        setFilters(list)
    }

    const clearSelectedClauseType = (index: number) => {
        const filter = filters[index]
        filter.clauseType = undefined

        const list = [...filters]
        setFilters(list)
    }

    const setSelectedValue = (value: string | number, index: number, addFunc: any) => {
        try {
            const filter = filters[index]
            addFunc(filter.values, value)

            const list = [...filters]
            setFilters(list)
        } catch (e: any) {
            setError(e.error)
        }
    }

    const clearSelectedValue = (index: number) => {
        const filter = filters[index]
        filter.values = []

        const list = [...filters]
        setFilters(list)
    }

    const addRow = () => {
        const list = [...filters]
        list.push(emptyFilter())
        setFilters(list)
    }

    const removeRow = (index: number) => {
        const list = [...filters]
        list.splice(index, 1)
        setFilters(list)
    }

    const addSingleValue = (values: (string | number)[], value: string | number) => value ? values[0] = value : values.pop()
    const addMultiValue = (values: (string | number)[], value: string | number) => values.push(value)
    const removeMultiValue = (values: (string | number)[], value: string | number) => values.splice(values.indexOf(value), 1)

    const renderDayParting = () => (
        <div className="filter-items">
            {filters.length == 0 && <Button title="Add new filter" onClick={addRow}><PlusOutlined /></Button>}
            <div>
                {filters.map((rule, index) => <div className="filter-item" key={index}>
                    <Select
                        showSearch
                        allowClear
                        optionFilterProp="title"
                        placeholder="Select filter"
                        value={rule.type}
                        onSelect={value => setSelectedFilter(value, index)}
                        onClear={() => clearSelectedFilter(index)}
                        className="filter-selector ant-item-first"
                    >
                        {Array.from(keyLoadersMap).map(([key]) => <Option title={key} key={key} value={key}>{enumView(key)}</Option>)}
                    </Select>
                    <Select
                        showSearch
                        allowClear
                        optionFilterProp="title"
                        placeholder="Select type"
                        value={rule.clauseType}
                        onSelect={value => setSelectedClauseType(value, index)}
                        onClear={() => clearSelectedClauseType(index)}
                        className="filter-clause-type ant-item-second"
                    >
                        {Object.values([clauseTypeEnum.In, clauseTypeEnum.NotIn]).map(key => <Option title={key} key={key} value={key}>{getClauseTypeLabel(key)}</Option>)}
                    </Select>
                    {(!rule.loadedData || rule.loadedData.length == 0) ? <Input
                        allowClear
                        placeholder="Enter value"
                        className="filter-value ant-item-second"
                        onChange={e => setSelectedValue(e.target.value, index, addSingleValue)}
                        value={rule.values?.[0] as any}
                    /> : <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        optionFilterProp="title"
                        placeholder="Select value"
                        value={rule.values}
                        onSelect={value => setSelectedValue(value, index, addMultiValue)}
                        onDeselect={value => setSelectedValue(value, index, removeMultiValue)}
                        onClear={() => clearSelectedValue(index)}
                        className="filter-value ant-item-second"
                    >
                        {rule.loadedData.map((item: FilterData, i) => <Option title={item.name} key={i} value={item.id ?? item.name}>{item.name}</Option>)}
                    </Select>}

                    <Button title="Remove filter" className="ant-item-second" type="text" onClick={() => removeRow(index)}><MinusOutlined /></Button>
                    {index == filters.length - 1 && <Button title="Add new filter" type="text" className="ant-item-second" onClick={addRow}><PlusOutlined /></Button>}
                </div>)}
            </div>
        </div>
    )

    return <FormItem label="Filters">
        {error ? <Alert message={String(error)} type="error" /> : null}
        {renderDayParting()}
    </FormItem>
}

export default Filters


// WEBPACK FOOTER //
// ./src/component/app/report/Filters.tsx